import { inject, Injectable } from '@angular/core';
import { AppConfigService } from '@app/config/config.service';
import { GoogleTagManagerService, ThemeService, SawDust } from '@cudirect/shared-ui-library';
import { LenderService } from '@shared/service/lender/lender.service';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { firstValueFrom } from 'rxjs';

/**
 * Initializer Lender data after getting lender config
 */
@Injectable({
  providedIn: 'root'
})
export class InitializeLenderService {
  private logger = inject(SawDust).withContext({ component: 'InitializeLenderService' });
  constructor(
    private readonly lenderService: LenderService,
    private readonly appConfigService: AppConfigService,
    private readonly gtm: GoogleTagManagerService,
    private readonly themeService: ThemeService
  ) {}

  /**
   * Sets the lender-specific configurations such as theme settings and analytics.
   *
   * @param lenderDataInfo - Contains the lender's configuration such as theme colors, ADA level, and logo URL.
   */
  public async setLenderConfigData(lenderDataInfo: LenderConfiguration): Promise<void> {
    try {
      // Get app config - note that we can capture the resulting config here if it's needed in this method
      const data = await firstValueFrom(this.appConfigService.get());

      // This is overridden elsewhere in the application, but it gives us a baseline
      this.lenderService.lenderConfigData = lenderDataInfo;
      this.themeService.setTheme(lenderDataInfo.mainColor, lenderDataInfo.adaLevel || 'aa', lenderDataInfo.lenderLogoUrl);

      // Set Lender Specific GTM (Global DX GTM is set in index.html)

      // Read googleAnalytics object
      if (lenderDataInfo?.googleAnalytics) {
        // Check googleAnalytics object, if available then read the accountid value.
        const googleAnalysticsValue =
          lenderDataInfo?.googleAnalytics && lenderDataInfo?.googleAnalytics?.isEnabled === true
            ? lenderDataInfo?.googleAnalytics?.accountId
            : '';
        if (googleAnalysticsValue) {
          this.logger.info('Added GA in Lender Initialization');
          this.gtm.addGA(googleAnalysticsValue);
        }
      }

      // Read googleTagManager object
      if (lenderDataInfo?.googleTagManager) {
        // Check googleTagManager object, if available then read the accountid value.
        const googleTagManagerValue =
          lenderDataInfo?.googleTagManager && lenderDataInfo?.googleTagManager?.isEnabled === true
            ? lenderDataInfo?.googleTagManager?.accountId
            : '';
        if (googleTagManagerValue) {
          this.logger.info('Added Lender GTM in Lender Initialization');

          /** Grab the default environmental allow/block lists from config.json
           * Full list of GTM Tag IDs, visit https://developers.google.com/tag-platform/tag-manager/restrict
           * Default to the global allow/block lists if not set in the lender config
           * This is used to restrict the GTM tags that are loaded
           * */
          const allow = lenderDataInfo?.googleTagManager?.allowList
            ? lenderDataInfo?.googleTagManager?.allowList
            : data?.lenderGTM?.allowList;
          const block = lenderDataInfo?.googleTagManager?.blockList
            ? lenderDataInfo?.googleTagManager?.blockList
            : data?.lenderGTM?.blockList;

          // We can consider passing in a different dataLayer name specific to the Lender Owned GTM
          this.gtm.addGTM(googleTagManagerValue, 'dataLayer', null, allow, block);
        }
      }

      // Capture errors in the console if the lender config data is not set
    } catch (error) {
      this.logger.withError(error).error('Error setting lender config data');
    }
  }
}
